import { ApiRx } from "@polkadot/api";
import { RuntimeVersion } from "@polkadot/types/interfaces";
import { Struct } from "@polkadot/types-codec";
import { firstValueFrom, lastValueFrom } from "rxjs";

type DeviceSdkParamsV2 = { deviceKeyIdentifier: string };

export const getFacetecDeviceSdkParamsV2 = async (
  api: ApiRx,
): Promise<DeviceSdkParamsV2> => {
  const { deviceKeyIdentifier } = await lastValueFrom(
    api.rpc.bioauth.getFacetecDeviceSdkParamsV2(),
  );

  const params = {
    deviceKeyIdentifier: deviceKeyIdentifier.toString(),
  };

  return params;
};

export type StepV1Status<Done> =
  | { tag: "continue"; data: { context: string } }
  | { tag: "done"; data: Done };

export type StepV1Result<Done> = {
  responseBlob: string;
  status: StepV1Status<Done>;
};

export type EnrollDone = Record<string, never>;

export type AuthenticateDone = {
  authTicket: Uint8Array;
  authTicketSignature: Uint8Array;
};

export const enrollStepV1 = async (
  api: ApiRx,
  requestBlob: string,
  context?: string,
): Promise<StepV1Result<EnrollDone>> => {
  const { responseBlob, status } = await lastValueFrom(
    api.rpc.bioauth.enrollStepV1({ requestBlob, context }),
  );

  return {
    responseBlob: responseBlob.toString(),
    status: status.isContinue
      ? {
          tag: "continue",
          data: { context: status.asContinue.context.toString() },
        }
      : { tag: "done", data: {} },
  };
};

export const authenticateStepV1 = async (
  api: ApiRx,
  requestBlob: string,
  context?: string,
): Promise<StepV1Result<AuthenticateDone>> => {
  const { responseBlob, status } = await lastValueFrom(
    api.rpc.bioauth.authenticateStepV1({ requestBlob, context }),
  );

  return {
    responseBlob: responseBlob.toString(),
    status: status.isContinue
      ? {
          tag: "continue",
          data: { context: status.asContinue.context.toString() },
        }
      : {
          tag: "done",
          data: {
            authTicket: status.asDone.authTicket.toU8a(),
            authTicketSignature: status.asDone.authTicketSignature.toU8a(),
          },
        },
  };
};

export const rotateKeys = async (api: ApiRx): Promise<string> =>
  await lastValueFrom(api.rpc.author.rotateKeys()).then((bytes) =>
    bytes.toJSON(),
  );

export const setKeys = async (api: ApiRx, bytes: string): Promise<void> => {
  await lastValueFrom(
    api.rpc.authorExt.setKeys(api.createType("Bytes", bytes)),
  );
};

type RuntimeVersionJSON = {
  [Key in Exclude<keyof RuntimeVersion, keyof Struct>]: string;
};
export const getRuntimeVersion = (api: ApiRx): Promise<RuntimeVersionJSON> =>
  firstValueFrom(api.rpc.state.getRuntimeVersion()).then(
    (runtimeVersion) => runtimeVersion.toJSON() as RuntimeVersionJSON,
  );

export const getSystemChain = (api: ApiRx): Promise<string> =>
  firstValueFrom(api.rpc.system.chain()).then((text) => text.toString());
